import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { getToastMessage } from '@rsos/components/capstone/ConversationsToast/helpers';
import { SOURCE_TCAPI } from '@rsos/constants/locationSources';
import { CONVERSATION_START } from '@rsos/hermes-js/src/typings/conversation';
import { conversationStateValues } from '@rsos/reduxDataStore/src/features/conversations/conversations.typings';
import { useCreateTCAPIConversationMutation } from '@rsos/reduxDataStore/src/features/conversations/tcapiAPI';
import {
  setSelectedCallerID,
  StateWithEmergencyCalls,
} from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { setSelectedTab } from '@rsos/reduxDataStore/src/features/eventPane/eventPaneSlice';
import { useCurrentOrgName } from '@rsos/sinatra/src/selectors';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import { dismissToastByID, notifyConversation } from '@rsos/utils/toastUtils';
import { RootState } from '../app';

const useAllConversationToastNotifications = () => {
  const { callQueue, selectedCallerID, numberWithNewLocation } = useSelector(
    (state: StateWithEmergencyCalls) => state.emergencyCalls,
  );

  const conversations = useSelector((state: RootState) => state.conversations);

  const dispatch = useDispatch();
  const [createTCAPIConversation] = useCreateTCAPIConversationMutation();
  const orgName = useCurrentOrgName();
  const customButtonSources = new Set([SOURCE_TCAPI]);

  const handleButtonClick = (conversation: conversationStateValues) => {
    if (conversation.source === SOURCE_TCAPI) {
      createTCAPIConversation({
        callerID: numberWithNewLocation,
        orgName: orgName,
      });
      dispatch(setSelectedCallerID(numberWithNewLocation));
      dispatch(setSelectedTab('chat'));
    }
  };

  // Show toast if a number comes in that is notifiable
  useEffect(() => {
    if (numberWithNewLocation) {
      const notifiedCall = callQueue[numberWithNewLocation];
      const mostRecentLocation = getMostRecentLoc(notifiedCall);
      const hasCustomButton = customButtonSources.has(
        mostRecentLocation?.source || '',
      );

      const conversation = conversations[numberWithNewLocation];

      if (
        getToastMessage(conversation?.source) &&
        conversation.recentMessageType === CONVERSATION_START
      ) {
        notifyConversation({
          conversation: conversation,
          ...(hasCustomButton && {
            handleButtonClick: () => handleButtonClick(conversation),
          }),
          options: {
            toastId: `conversations-toast-${numberWithNewLocation}`,
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- numberWithNewLocation is persistent
  }, [numberWithNewLocation]);

  // Remove toast if its id matches selectedCallerID
  useEffect(() => {
    if (selectedCallerID) {
      dismissToastByID(`conversations-toast-${selectedCallerID}`);
    }
  }, [selectedCallerID]);
};

export default useAllConversationToastNotifications;
